import { useSnackbar } from '@automata/rexports/notistack';
import {
  ErrorDisplay,
  Modal,
  TextField,
  TrackedButton,
  Typography,
  WarningCard,
} from '@automata/ui';
import { useSaveWorkflow } from '@automata/wfb-api';
import Box from '@mui/system/Box';
import Stack from '@mui/system/Stack';
import { useRouter } from 'next/router';
import { useCallback, useDeferredValue, useEffect, useState } from 'react';

import { selectWorkflowIsSaved } from '../../features/ui/selectors';
import { setWorkflowMetadata } from '../../features/workflow/workflowSlice';
import { selectWorkflowJsonToSave } from '../../features/workflowJsonSelectors';
import { useAppDispatch, useAppSelector } from '../../hooks';

interface Props {
  onClose: () => void;
  open: boolean;
  workflowName: string;
}

export const DuplicateWorkflowModal = ({
  workflowName,
  open,
  onClose,
}: Props): JSX.Element | null => {
  const router = useRouter();
  const workflowIsSaved = useAppSelector(selectWorkflowIsSaved);
  const deferredWorkflowName = useDeferredValue(workflowName);
  const deferredWorkflowIsSaved = useDeferredValue(workflowIsSaved);

  const { enqueueSnackbar } = useSnackbar();
  const workflowJsonToSave = useAppSelector(selectWorkflowJsonToSave);

  const dispatch = useAppDispatch();

  const [newName, setNewName] = useState(`Copy of ${workflowName}`);
  // TODO(RD, 04/09/2024): this is a horrible hack to work around the fact that when loading a workflow
  // we initially render it with an empty redux store (where the workflow name is "Untitled Workflow")
  // before rendering it again once the workflow (and its correct name) has been loaded into the redux store.
  // This will reset the field if the workflow name changes, but this should only happen on load as once this
  // modal is open, there's no other way for the user to rename the workflow.
  // The need for this will go away if we implement an atomic workflow loading hook as suggested in this comment:
  // https://github.com/automata-tech/prototype-frontend-editor/pull/350#discussion_r1742580748
  useEffect(() => {
    setNewName(`Copy of ${deferredWorkflowName}`);
  }, [deferredWorkflowName]);

  const {
    saveWorkflow,
    result: { error },
  } = useSaveWorkflow({
    onSuccess: (data) => {
      router.push({ query: { ...router.query, id: data.id } });
      enqueueSnackbar('Workflow duplicated', { variant: 'success' });
      dispatch(setWorkflowMetadata(data));
    },
  });

  const onSubmit = useCallback(async () => {
    saveWorkflow({ ...workflowJsonToSave, name: newName });
  }, [saveWorkflow, workflowJsonToSave, newName]);

  return (
    <Modal
      onClose={onClose}
      open={open}
      title={`Duplicate Workflow: "${workflowName}"`}
    >
      <Box>
        <Stack gap={2}>
          <Typography>Enter a new workflow name</Typography>
          <TextField
            label="Workflow name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </Stack>
        {!deferredWorkflowIsSaved && (
          <Box paddingY={2}>
            <WarningCard message="Please note this action saves changes to your current workflow" />
          </Box>
        )}

        {error !== null && (
          <ErrorDisplay error={error} userMessage="Failed to save workflow" />
        )}
        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
          <TrackedButton
            trackLabel="screen.visualWorkflow.duplicateCancel"
            color="secondary"
            variant="text"
            onClick={onClose}
          >
            Cancel
          </TrackedButton>
          <TrackedButton
            trackLabel="screen.visualWorkflow.duplicate"
            variant="contained"
            type="submit"
            onClick={onSubmit}
          >
            Duplicate
          </TrackedButton>
        </Stack>
      </Box>
    </Modal>
  );
};
